import { format, max, min, parseISO } from "date-fns";
import { isArray } from "lodash";

/**
 * Function generating a preview of date range to render in components. If only one date is provided,
 * it returns it formatted without generating range.
 * @param {array} - a collection of objects with dates in string ISO format.
 * @returns {string} - readable dates in dd.mm.yyyy format.
 */
export default (times: { date: string }[]) => {
  if (!isArray(times)) return null;

  if (times.length === 1) return format(new Date(times[0].date), "dd.MM.yyyy");

  const dates = times.map((obj) => parseISO(obj.date));
  const earliestDate = min(dates);
  const latestDate = max(dates);

  return `${format(earliestDate, "dd.MM.yyyy")} - ${format(
    latestDate,
    "dd.MM.yyyy"
  )}`;
};

/**
 * Function generating a preview of date range from Date objects to render in components.
 * If only one date is provided (second element is null), it returns it formatted without generating range.
 * @param {array} dateRange - Array containing start and end dates [Date, Date | null]
 * @returns {string | null} - readable dates in dd.MM.yyyy format or null if invalid input
 */
export const formatDateRangePreviewFromDates = (
  dateRange: [Date, Date | null] | [Date | null, Date | null]
): string | null => {
  if (!isArray(dateRange) || dateRange.length !== 2) return null;

  const [startDate, endDate] = dateRange;

  if (!startDate || !(startDate instanceof Date)) return null;

  // If only one date is provided
  if (!endDate) return format(startDate, "dd.MM.yyyy");

  if (!(endDate instanceof Date)) return null;

  return `${format(startDate, "dd.MM.yyyy")} - ${format(
    endDate,
    "dd.MM.yyyy"
  )}`;
};

/**
 * Function generating a preview of date and time range to render in Absence Request preview.
 * Takes the first entry from the array and formats it as a single date with time range.
 * @param times - An array of objects containing date and optional time information. Only the first element is used.
 * @param times[].date - The date in ISO string format.
 * @param times[].startTime - Optional start time in HH:mm:ss format.
 * @param times[].endTime - Optional end time in HH:mm:ss format.
 * @returns A formatted string in "dd.MM.yyyy HH:mm-HH:mm" format, or null if the input is invalid.
 */
export const formatHourRangePreview = (
  times: {
    date: string;
    startTime?: string;
    endTime?: string;
  }[]
) => {
  if (!isArray(times)) return null;

  const { date, startTime, endTime } = times[0];
  const formattedDate = format(parseISO(date), "dd.MM.yyyy");
  // Extract HH:mm from HH:mm:ss
  const formattedStartTime = startTime?.slice(0, 5);
  const formattedEndTime = endTime?.slice(0, 5);

  return `${formattedDate} ${formattedStartTime}-${formattedEndTime}`;
};
